import {
  Component,
  OnDestroy,
  OnInit,
  QueryList,
  ViewChild,
  ViewChildren,
} from '@angular/core';
import { ActivatedRoute } from '@angular/router';
import { Subject } from 'rxjs';
import { Conversation } from 'src/app/interfaces/conversation';
import { Message } from 'src/app/interfaces/message';
import { ApiService } from 'src/app/services/api.service';
import { environment } from 'src/environments/environment';
import {
  tap,
  debounceTime,
  distinctUntilChanged,
  switchMap,
  take,
} from 'rxjs/operators';
import { Inventory } from 'src/app/interfaces/inventory';
import { WebSocketService } from 'src/app/services/web-socket.service';
import { BrowserStorageService } from 'src/app/services/browser-storage.service';
import { SessionService } from 'src/app/services/session.service';
import { StoreContextService } from 'src/app/services/store-context.service';
import {
  ConversationCacheService,
  filterAllowedMessages,
} from 'src/app/services/conversation-cache.service';
import { ChatTileComponent } from '../chat-tile/chat-tile.component';
import { ChatroomComponent } from '../chatroom/chatroom.component';
import { RefreshPhase } from 'src/app/directives/refresh-surface.directive';

@Component({
  selector: 'app-layout',
  templateUrl: './layout.component.html',
  styleUrls: ['./layout.component.scss'],
})
export class LayoutComponent implements OnInit, OnDestroy {
  conversations: Conversation[] = [];
  selectedConversation!: Conversation;
  inventories: Inventory[] = [];
  current_inventory!: number;
  loading = true;
  IsLoadingCustomers: boolean = false;
  signedToken: string | null = '';
  businessesName: string | null = 'Wink GPT';
  businessesLogo: string | null = '';
  page: number = 0;
  quantity: number = 100;
  loadingChats: boolean = true;
  loadMoreChats: boolean = true;
  conversationsCount: number = 0;
  searchValue: string = '';
  private searchTerm = '';
  private conversationsRequestSeq = 0;
  private searchSubject: Subject<string> = new Subject();
  socketMessage = {} as Message;
  totalAssitanceRequired: number = 0;
  refreshPhase: RefreshPhase = 'idle';
  refreshStatusMessage = '';
  private storageManager: BrowserStorageService = new BrowserStorageService();
  private refreshRevealTimer?: ReturnType<typeof setTimeout>;

  @ViewChildren(ChatTileComponent)
  tiles: QueryList<ChatTileComponent> = new QueryList();

  @ViewChild(ChatroomComponent)
  chatroom?: ChatroomComponent;

  constructor(
    private api: ApiService,
    private route: ActivatedRoute,
    private session: SessionService,
    private webSocketService: WebSocketService,
    private storeContext: StoreContextService,
    private conversationCache: ConversationCacheService
  ) {}

  async ngOnInit(): Promise<void> {
    try {
      setTimeout(async () => {
        // Valida el token de session storage o URL para autenticar al usuario
        await this.validateToken();

        // Obtener nombre de la empresa
        await this.getBusinessName();

        // Obtener el diseño de la empresa
        await this.getDesignLayout();
      });

      this.searchSubject
        .pipe(
          tap(() => (this.IsLoadingCustomers = true)),
          debounceTime(300),
          distinctUntilChanged(),
          switchMap(searchText => this.filterConversations(searchText))
        )
        .subscribe();
    } catch (error) {
      console.error(error);
      window.location.href = environment.landing_page_url;
    }
  }

  ngOnDestroy(): void {
    if (this.refreshRevealTimer) {
      clearTimeout(this.refreshRevealTimer);
    }
  }

  get refreshInProgress(): boolean {
    return this.refreshPhase !== 'idle';
  }

  private async getBusinessName() {
    const businessesName = this.storageManager.get('name', 'session');
    if (!businessesName) {
      this.businessesName = this.route.snapshot.queryParamMap.get('name');
      if (this.businessesName) {
        this.storageManager.set('name', this.businessesName, 'session');
      }
    } else {
      this.businessesName = businessesName;
    }
  }

  //  Si el token no se encuntra en el session storage se obtiene de el param url, de no encontrarlo,
  //  redirecciona a el landing de wink. Si por el caso contrario se encuentra, este se guarda en
  //  el Session Storage. Pero si el token si existe en el session storage, se procede a validarlo.
  private async validateToken() {
    const signedToken = this.storageManager.get('signedToken', 'session');
    if (!signedToken) {
      this.assignTokenFromQueryParam();
    }

    await this.validateExistingToken();
  }

  private assignTokenFromQueryParam() {
    this.signedToken = this.route.snapshot.queryParamMap.get('signed_token');

    // Redirecciona a la pagina de wink si no existe signed_token
    if (!this.signedToken) {
      window.location.href = environment.landing_page_url;
      return;
    }

    // Si el token existe en queryParam, se guarda en session storage
    this.session.setToken(this.signedToken);
    return;
  }

  private async validateExistingToken() {
    try {
      const res = await this.api.validateChatToken(this.session.getToken());
      this.businessesName = res.name;
      this.inventories = res.inventories;
      this.storeContext.inventories = res.inventories ?? [];
      if (this.storageManager.get('current_inventory')) {
        this.current_inventory = parseInt(
          this.storageManager.get('current_inventory')!
        );
      } else {
        this.current_inventory = 0;
        this.storageManager.set('current_inventory', '0');
      }
      this.loadConversations();
      // La conexion se inicializa aqui (y no antes) porque la URL del socket
      // necesita el signedToken ya validado y guardado en session storage
      this.webSocketService.initConnection();
      this.webSocketService.messages$.subscribe({
        next: async msg => {
          if (
            msg.action === 'is_assistance_required' ||
            msg.action === 'is_bot_disabled'
          ) {
            this.removeTag(
              msg.action,
              msg.conversationId,
              msg.status,
              msg.step
            );
            this.getTotalAssistanceRequired();
            return;
          }
          let flagChangeConversationId = false;
          let newConversationId = '';
          const auxSelectedConversationId = this.selectedConversation?.id;
          if (
            this.conversations.filter(
              conversation => conversation.id === msg.conversationId
            ).length === 0
          ) {
            // El evento del socket no trae el telefono, y gpt/chat/:id
            // responde la conversacion sin phone ni customer (mapeo roto en
            // wink-store-backend), asi que no sirve para deduplicar. Se
            // recarga la lista (gpt/chats si viene completa) y se ubica alli
            // la conversacion nueva.
            const resConversations = await this.api.getConversations(
              0,
              200,
              this.current_inventory,
              this.searchValue,
              this.storageManager.get('isAssistanceRequired')
                ? this.storageManager.get('isAssistanceRequired')!
                : 'all'
            );
            this.conversations = this.preserveKnownCustomers(
              resConversations.conversations
            );

            const newConversation = this.conversations.find(
              c => c.id === msg.conversationId
            );
            // Mismo telefono que el chat abierto: es la misma persona con un
            // conversationId nuevo; se actualiza el id para que el chat
            // abierto siga recibiendo los mensajes en vivo
            if (
              newConversation &&
              this.selectedConversation &&
              newConversation.phone === this.selectedConversation.phone &&
              newConversation.id !== this.selectedConversation.id
            ) {
              flagChangeConversationId = true;
              newConversationId = newConversation.id;
            }
          }
          const index = this.conversations.findIndex(
            obj => obj.id === msg.conversationId
          );
          let targetConversation: Conversation | undefined;
          if (index > -1) {
            const [conversation] = this.conversations.splice(index, 1);
            if (flagChangeConversationId) {
              conversation.id = newConversationId;
            }
            conversation.step = msg.step;
            conversation.is_read = false;
            conversation.date = new Date(msg.epoch).toISOString();
            this.conversations.unshift(conversation);
            targetConversation = conversation;
          }
          if (
            this.selectedConversation &&
            (msg.conversationId === this.selectedConversation.id ||
              msg.conversationId === auxSelectedConversationId ||
              flagChangeConversationId)
          ) {
            if (flagChangeConversationId) {
              this.selectedConversation.id = newConversationId;
            }
            this.selectedConversation.step = msg.step;
            this.selectedConversation.is_read = true;
            this.socketMessage = msg;
            this.api.updateConversation(this.selectedConversation.id);
          }

          // Alimenta la cache de mensajes para cualquier conversacion (sea o
          // no la seleccionada) reutilizando la conversacion ya localizada
          // arriba al reordenar this.conversations
          if (msg.type) {
            const phone = targetConversation?.phone;
            if (phone) {
              const appended = this.conversationCache.appendMessage(phone, msg);
              const isSelected = this.selectedConversation?.phone === phone;
              if (!appended && !isSelected) {
                this.prefetchConversationHistory(phone);
              }
            }
          }
        },
      });
    } catch (error) {
      // Muestra un error en consola y redirige a la landing del producto
      // cuando no se pueda validar el token
      console.error(error);
      window.location.href = environment.landing_page_url;
    }
  }

  // Al iniciar un pedido nuevo el publisher crea otra conversacion para el
  // mismo telefono con customer_id aun sin vincular, y la lista devuelve esa
  // fila (la mas reciente). Se conserva el cliente ya conocido en esta sesion
  // para que el chat no se degrade a "Usuario no registrado".
  private preserveKnownCustomers(incoming: Conversation[]): Conversation[] {
    const known = new Map(
      this.conversations.filter(c => c.customer).map(c => [c.phone, c])
    );
    return incoming.map(conversation => {
      if (conversation.customer) {
        return conversation;
      }
      const previous = known.get(conversation.phone);
      return previous
        ? {
            ...conversation,
            customer: previous.customer,
            customer_id: conversation.customer_id ?? previous.customer_id,
          }
        : conversation;
    });
  }

  // Trae en background el historial de un telefono aun sin cache y lo
  // siembra; protegido por pendingPrefetch para no duplicar requests si
  // llegan varios mensajes seguidos del mismo telefono
  private async prefetchConversationHistory(phone: string): Promise<void> {
    if (!this.conversationCache.tryLockPrefetch(phone)) return;
    try {
      const res = await this.api.getConversationHistoryByPhone(phone, 0, 20);
      const messages = filterAllowedMessages(res.messages ?? []);
      this.conversationCache.set(phone, {
        messages,
        page: 0,
        loadMoreMessages: true,
      });
    } catch (error) {
      console.error(error);
    } finally {
      this.conversationCache.unlockPrefetch(phone);
    }
  }

  // Precarga los primeros chats de la lista al abrir el panel para que al
  // hacer click ya tengan historial en cache; son requests independientes
  // asi que se disparan en paralelo (acotado a 5 candidatos)
  private async prefetchInitialConversations(): Promise<void> {
    const candidates = this.conversations
      .slice(0, 5)
      .filter(conversation => !this.conversationCache.has(conversation.phone));
    await Promise.allSettled(
      candidates.map(conversation =>
        this.prefetchConversationHistory(conversation.phone)
      )
    );
  }

  // Sin trackBy Angular recrea todos los tiles al reemplazar el array en cada
  // refresh/socket, re-disparando la animacion chip-in en toda la lista.
  trackByPhone(_index: number, conversation: Conversation): string {
    return conversation.phone;
  }

  private scrollToChat(phone: string) {
    this.tiles.changes.pipe(take(1)).subscribe(() => {
      const chatElement = this.tiles.find(
        chat => chat.conversation.phone === phone
      );
      if (chatElement) {
        const element = document.getElementById(
          `conversation-${chatElement.conversation.phone}`
        );
        if (element) {
          element.scrollIntoView({ behavior: 'smooth', block: 'start' });
        }
      }
    });
  }

  private getConversationByQueryParamPhone(): void {
    const phone = this.route.snapshot.queryParamMap.get('phone');

    if (!phone) {
      return;
    }

    const conversation = this.conversations.find(
      conversation => conversation.phone === phone
    );

    if (!conversation) {
      return;
    }

    this.selectConversation(conversation);
    this.scrollToChat(phone);
  }

  async loadConversations(preserveChat: boolean = false): Promise<void> {
    //this.loading = true;
    try {
      const requestSeq = ++this.conversationsRequestSeq;
      this.IsLoadingCustomers = true;
      const res = await this.api.getConversations(
        this.page,
        this.quantity,
        this.current_inventory,
        this.searchTerm,
        this.storageManager.get('isAssistanceRequired')
          ? this.storageManager.get('isAssistanceRequired')!
          : 'all'
      );
      if (requestSeq !== this.conversationsRequestSeq) return;
      this.conversations = this.preserveKnownCustomers(res.conversations);
      // Almacenar en session la conversacion mas reciente si no se tiene
      if (this.conversations.length > 0) {
        const selectedConversation = this.storageManager.get(
          'selectedConversation',
          'session'
        );

        if (!selectedConversation) {
          const conversationData = JSON.stringify(res.conversations[0]);

          this.storageManager.set(
            'selectedConversation',
            conversationData,
            'session'
          );

          this.selectedConversation = JSON.parse(conversationData);
        } else if (!this.selectedConversation) {
          // Restaura la conversacion seleccionada tras recargar la pagina;
          // sin esto quedaba undefined y el handler del socket fallaba
          // en cada mensaje entrante
          this.selectedConversation = JSON.parse(selectedConversation);
        }
      }

      this.getTotalAssistanceRequired();
      this.conversationsCount = res.pagination.total;
      this.loadMoreChats = this.conversations.length < this.conversationsCount;
      this.loadingChats = false;
      if (this.page === 0 && !this.searchTerm) {
        // No se espera esta promesa: es un precalentamiento de cache en
        // background, no debe bloquear el render de la lista
        this.prefetchInitialConversations();
      }
    } catch (error) {
      console.error(error);
    } finally {
      this.loading = false;
      this.IsLoadingCustomers = false;

      this.getConversationByQueryParamPhone();
    }
  }

  async getTotalAssistanceRequired(): Promise<void> {
    try {
      const res = await this.api.getTotalAssistanceRequired(
        this.current_inventory
      );
      this.totalAssitanceRequired = res.total;
    } catch (error) {
      console.error(error);
      this.loading = false;
    }
  }

  async refreshConversationsAndChat(): Promise<void> {
    if (this.refreshInProgress) return;

    this.refreshPhase = 'loading';
    this.refreshStatusMessage = 'Actualizando conversaciones y chat';
    const requestSeq = ++this.conversationsRequestSeq;
    const selectedPhone = this.selectedConversation?.phone;
    // Mantiene la profundidad cargada sin cambiar los limites de las paginas
    // siguientes del scroll infinito (page sigue expresada en bloques de quantity).
    const requestedSize = this.quantity * (this.page + 1);
    const assistanceFilter =
      this.storageManager.get('isAssistanceRequired') ?? 'all';

    const conversationsPromise = this.api.getConversations(
      0,
      requestedSize,
      this.current_inventory,
      this.searchTerm,
      assistanceFilter
    );
    const totalPromise = this.api.getTotalAssistanceRequired(
      this.current_inventory
    );

    let refreshFailed = false;
    let conversationsValue: Awaited<typeof conversationsPromise> | undefined;
    try {
      conversationsValue = await conversationsPromise;
    } catch (error) {
      refreshFailed = true;
      console.error(error);
    }

    if (conversationsValue && requestSeq === this.conversationsRequestSeq) {
      const incoming = this.preserveKnownCustomers(
        conversationsValue.conversations
      );
      const refreshedSelection = selectedPhone
        ? incoming.find(conversation => conversation.phone === selectedPhone)
        : undefined;

      if (refreshedSelection && this.selectedConversation) {
        Object.assign(this.selectedConversation, refreshedSelection);
        this.conversations = incoming.map(conversation =>
          conversation.phone === selectedPhone
            ? this.selectedConversation
            : conversation
        );
        this.storageManager.set(
          'selectedConversation',
          JSON.stringify(this.selectedConversation),
          'session'
        );
      } else {
        this.conversations = incoming;
        if (incoming.length > 0) {
          this.selectConversation(incoming[0]);
        } else {
          this.storageManager.remove('selectedConversation', 'session');
          this.selectedConversation = undefined as unknown as Conversation;
        }
      }

      this.conversationsCount = conversationsValue.pagination.total;
      this.loadMoreChats = this.conversations.length < this.conversationsCount;
    }

    const selectedPhoneWasPreserved =
      !!selectedPhone && this.selectedConversation?.phone === selectedPhone;
    const chatPromise = selectedPhoneWasPreserved
      ? (this.chatroom?.refreshConversationData() ?? Promise.resolve())
      : Promise.resolve();
    const [totalResult, chatResult] = await Promise.allSettled([
      totalPromise,
      chatPromise,
    ]);

    if (
      totalResult.status === 'fulfilled' &&
      requestSeq === this.conversationsRequestSeq
    ) {
      this.totalAssitanceRequired = totalResult.value.total;
    } else if (totalResult.status === 'rejected') {
      refreshFailed = true;
      console.error(totalResult.reason);
    }
    if (chatResult.status === 'rejected') {
      refreshFailed = true;
      console.error(chatResult.reason);
    }

    this.revealRefreshedContent(refreshFailed);
  }

  private revealRefreshedContent(refreshFailed: boolean): void {
    this.refreshPhase = 'revealing';
    this.refreshStatusMessage = refreshFailed
      ? 'No se pudo actualizar todo el contenido'
      : 'Conversaciones y chat actualizados';
    this.refreshRevealTimer = setTimeout(() => {
      this.refreshPhase = 'idle';
      this.refreshStatusMessage = '';
      this.refreshRevealTimer = undefined;
    }, 180);
  }

  async loadMoreConversations(): Promise<void> {
    try {
      if (this.loadingChats || this.refreshInProgress) return;
      this.loadingChats = true;
      //this.IsLoadingCustomers = true;
      this.page++;
      const res = await this.api.getConversations(
        this.page,
        this.quantity,
        this.current_inventory,
        this.searchTerm,
        this.storageManager.get('isAssistanceRequired')
          ? this.storageManager.get('isAssistanceRequired')!
          : 'all'
      );

      this.conversations = this.conversations.concat(
        this.preserveKnownCustomers(res.conversations)
      );
      if (this.conversations.length === this.conversationsCount) {
        this.loadMoreChats = false;
      }
      this.getTotalAssistanceRequired();
      this.loading = false;
      this.loadingChats = false;
      this.IsLoadingCustomers = false;
    } catch (error) {
      console.error(error);
      this.loading = false;
      this.loadingChats = false;
      this.IsLoadingCustomers = false;
    }
  }

  selectConversation(conversation: Conversation): void {
    // El chatroom ya no se destruye al cambiar de chat (ver *ngIf en el
    // html), por eso esto es sincrono e inmediato: sin setTimeout ni await
    // bloqueante del bot status
    this.selectedConversation = conversation;
    this.selectedConversation.is_read = true;
    this.selectedConversation.phone = this.selectedConversation.phone.trim();
    this.api.updateConversation(this.selectedConversation.id);
    this.storageManager.set(
      'selectedConversation',
      JSON.stringify(conversation),
      'session'
    );

    const phone = this.selectedConversation.phone;
    this.api.getAiBotStatus(phone).then(res => {
      // El usuario pudo cambiar de chat mientras esta consulta resolvia
      if (this.selectedConversation?.phone !== phone) return;
      // Se muta el mismo objeto que recibe el chatroom por @Input (no una
      // copia), de lo contrario chatInputIsEnabled() no vería el cambio
      this.selectedConversation.is_bot_enabled = res.botEnabled;
      this.selectedConversation.step = res.step;
      const index = this.conversations.findIndex(
        obj => obj.id === this.selectedConversation.id
      );
      if (index > -1) {
        this.conversations[index] = { ...this.selectedConversation };
      }
    });
  }

  async selectInventory(inventory: number): Promise<void> {
    this.current_inventory = inventory;
    this.storageManager.set('current_inventory', inventory.toString());
    // El step/bot puede variar entre sedes; se limpia para evitar estados
    // mezclados aunque el historial de mensajes es el mismo por telefono
    this.conversationCache.clear();
    await this.loadConversations();
    this.selectedConversation = this.conversations[0];
    this.selectConversation(this.selectedConversation);
  }

  async filterConversations(searchText: string): Promise<void> {
    this.IsLoadingCustomers = true;
    this.page = 0;
    this.searchTerm = searchText.trim();
    await this.loadConversations(true);
    this.IsLoadingCustomers = false;
  }

  onSearchInput(event: Event) {
    const target = event.target as HTMLInputElement;
    const value = target ? target.value : '';
    this.searchSubject.next(value);
  }

  onConversationResolved(conversation: Conversation): void {
    this.removeTag(
      'is_assistance_required',
      conversation.id,
      false,
      conversation.step
    );
    this.getTotalAssistanceRequired();
  }

  async getDesignLayout(): Promise<void> {
    const res = await this.api.getDesign();
    const { logo_url } = res?.[0];
    this.businessesLogo = logo_url;
  }

  removeTag(
    action: string,
    conversationId: string,
    status: boolean,
    step: number
  ) {
    const conversation = this.conversations.find(
      conv => conv.id === conversationId
    );
    // selectedConversation puede ser un objeto distinto al de la lista
    // (selectConversation guarda una copia spread y al recargar se restaura
    // de session storage), por eso se actualizan sus flags aparte; de esto
    // depende que el input del chat se habilite sin recargar la pagina
    if (this.selectedConversation?.id === conversationId) {
      this.applyConversationStatus(
        this.selectedConversation,
        action,
        status,
        step
      );
      this.storageManager.set(
        'selectedConversation',
        JSON.stringify(this.selectedConversation),
        'session'
      );
    }
    if (conversation) {
      this.applyConversationStatus(conversation, action, status, step);
      if (
        action === 'is_assistance_required' &&
        status === false &&
        this.storageManager.get('isAssistanceRequired') === '1'
      ) {
        this.conversations = this.conversations.filter(
          c => c.id !== conversationId
        );
        if (this.conversations.length > 0) {
          this.selectConversation(this.conversations[0]);
        }
      }
    } else if (!(action === 'is_assistance_required' && status === false)) {
      this.conversations = [];
      this.page = 0;
      this.loadConversations();
    }
  }

  private applyConversationStatus(
    target: Conversation,
    action: string,
    status: boolean,
    step: number
  ): void {
    target.step = step;
    if (action === 'is_assistance_required') {
      target.is_assistance_required = status;
      if (!status) {
        target.is_bot_enabled = true;
      }
    } else {
      target.is_bot_enabled = status;
    }
  }
}
